import {
    Autocomplete,
    Box,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { formatDateForDisplay, isEventPast } from '@moment-golf/shared';
import { getSegmentedEventOptions } from '../../../shared/utils/eventDropdownUtils';
import { useTBContext } from '../../Contexts/useTBStates';
import { PushNotificationDialog } from '../PushNotificationDialog';
import { CommandCenterChecklistPopover } from './CommandCenterChecklistPopover';
import { useCommandCenterData } from './useCommandCenterData';
import { AttestScoresPanel } from './panels/AttestScoresPanel';
import { LeaderboardPanel } from './panels/LeaderboardPanel';
import { ChallengesPanel } from './panels/ChallengesPanel';
import { PlayoffPanel } from './panels/PlayoffPanel';
import { FirstTeePanel } from './panels/FirstTeePanel';
import { PlayerManagementPanel } from './panels/PlayerManagementPanel';
import { PaceOfPlayPanel } from './panels/PaceOfPlayPanel';

interface CommandCenterProps {
    appDims: any;
    compHeights: any;
}

const CC_PANELS = [
    'attestScores',
    'leaderboard',
    'challenges',
    'playoff',
    'firstTee',
    'playerManagement',
    'paceOfPlay',
] as const;

const QUICK_ACTIONS: { label: string; panel: string; group: 'Scoring' | 'Operations' }[] = [
    { label: 'Attest Scores', panel: 'attestScores', group: 'Scoring' },
    { label: 'Challenges', panel: 'challenges', group: 'Scoring' },
    { label: 'Leaderboard', panel: 'leaderboard', group: 'Scoring' },
    { label: 'Scorecard Playoff', panel: 'playoff', group: 'Scoring' },
    { label: 'First Tee', panel: 'firstTee', group: 'Operations' },
    { label: 'Pace of Play', panel: 'paceOfPlay', group: 'Operations' },
    { label: 'Player Management', panel: 'playerManagement', group: 'Operations' },
];

export const CommandCenter = ({ appDims, compHeights }: CommandCenterProps) => {
    const {
        factor,
        appTheme,
        viewPanel,
        setViewPanel,
        eventData,
        viewEvent,
        roundValue,
    } = useTBContext();

    const { handleSelectEvent } = useCommandCenterData();

    const isActivePanel = (CC_PANELS as readonly string[]).includes(viewPanel);

    const renderPanel = () => {
        switch (viewPanel) {
            case 'attestScores':
                return <AttestScoresPanel appDims={appDims} compHeights={compHeights} />;
            case 'leaderboard':
                return <LeaderboardPanel appDims={appDims} compHeights={compHeights} />;
            case 'challenges':
                return <ChallengesPanel appDims={appDims} compHeights={compHeights} />;
            case 'playoff':
                return <PlayoffPanel appDims={appDims} compHeights={compHeights} />;
            case 'firstTee':
                return <FirstTeePanel appDims={appDims} compHeights={compHeights} />;
            case 'playerManagement':
                return <PlayerManagementPanel appDims={appDims} compHeights={compHeights} />;
            case 'paceOfPlay':
                return <PaceOfPlayPanel appDims={appDims} compHeights={compHeights} />;
            default:
                return (
                    <LandingScreen
                        onSelectPanel={setViewPanel}
                    />
                );
        }
    };

    return (
        <Stack
            id='command-center-container'
            direction='column'
            spacing={0}
            sx={{
                width: '100%',
                height: '100%',
                alignItems: 'center',
                overflow: 'hidden',
                overscrollBehavior: 'none',
                backgroundColor: 'sepia.background',
            }}
        >
            {/* Shared event chrome */}
            <Stack
                id='command-center-event-header'
                direction='row'
                spacing={0.5}
                sx={{
                    width: compHeights.appUsableWidth,
                    maxHeight: 35,
                    alignItems: 'center',
                    padding: '10px 20px',
                    backgroundColor: 'primary.extraLight',
                    borderBottom: `1px solid ${appTheme.palette.primary.main}`,
                    boxShadow: 2,
                    flexShrink: 0,
                }}
            >
                <Stack
                    direction='row'
                    spacing={3}
                    sx={{
                        padding: '0px 10px',
                        display: 'flex',
                        flex: 1,
                        height: 35,
                        alignItems: 'center',
                    }}
                >
                    <Autocomplete
                        size='small'
                        options={getSegmentedEventOptions(eventData)}
                        getOptionLabel={(option: any) =>
                            (option?.startDate ? formatDateForDisplay(option.startDate) + ' - ' : '') +
                            (option?.eventName ?? '')
                        }
                        groupBy={(option: any) => (isEventPast(option?.endDate) ? 'Completed' : 'Upcoming')}
                        value={getSegmentedEventOptions(eventData).find((e) => e._id === viewEvent?._id) ?? null}
                        isOptionEqualToValue={(a, b) => a?._id === b?._id}
                        onChange={(_event, value) => handleSelectEvent(value)}
                        renderInput={(params) => <TextField {...params} />}
                        sx={{ width: '50%', margin: 1, padding: 0 }}
                    />
                    <Typography sx={{ fontSize: 12 * factor * 2 }}>@</Typography>
                    <Typography sx={{ fontSize: 18 * factor }}>
                        {viewEvent && viewEvent?.rounds[roundValue - 1]?.courseName}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ ml: 'auto', alignItems: 'center' }}>
                        <PushNotificationDialog />
                        <CommandCenterChecklistPopover />
                    </Stack>
                </Stack>
            </Stack>

            {/* Active panel or landing */}
            <Box
                sx={{
                    width: '100%',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                {isActivePanel && !viewEvent ? (
                    <Stack
                        sx={{
                            width: '100%',
                            height: '100%',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography variant='body1' color='sepia.light'>
                            Select an event to open this Command Center tab.
                        </Typography>
                    </Stack>
                ) : (
                    renderPanel()
                )}
            </Box>
        </Stack>
    );
};

const LandingScreen = ({ onSelectPanel }: { onSelectPanel: (panel: string) => void }) => {
    const scoringActions = QUICK_ACTIONS.filter((a) => a.group === 'Scoring');
    const opsActions = QUICK_ACTIONS.filter((a) => a.group === 'Operations');

    return (
        <Stack
            direction='column'
            spacing={3}
            sx={{
                width: '100%',
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                px: 4,
            }}
        >
            <Box
                sx={{
                    border: '1px solid',
                    borderColor: 'sepia.extraLight',
                    borderRadius: 2,
                    padding: 4,
                    textAlign: 'center',
                    maxWidth: 560,
                    width: '100%',
                }}
            >
                <Typography variant='h5' color='sepia.main' sx={{ fontWeight: 600, mb: 1 }}>
                    Command Center
                </Typography>
                <Typography variant='body1' color='sepia.light' sx={{ mb: 3 }}>
                    Scoring and Operations in one unified workspace. Select an event above.
                </Typography>
            </Box>
        </Stack>
    );
};
